import { mfaCodeSchema, type CurrentUser } from '@mfos/shared';
import { useMutation } from '@tanstack/react-query';
import { type SubmitEvent, useState } from 'react';
import { ErrorNotice } from '../../components/ErrorNotice';
import { TextField } from '../../components/Field';
import { ApiError } from '../../lib/api-client';
import { loginWithMfa } from './auth.api';
import { phrase } from '../../lib/i18n/runtime';

export interface MfaChallenge {
  readonly challengeToken: string;
  readonly expiresAt: string;
}

/** Second sign-in step: TOTP or recovery code for a pending MFA challenge. */
export function MfaStep({
  challenge,
  onAuthenticated,
  onCancel,
}: {
  challenge: MfaChallenge;
  onAuthenticated: (user: CurrentUser) => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | undefined>();
  const mutation = useMutation({
    mutationFn: async (value: string) => {
      const result = await loginWithMfa({ challengeToken: challenge.challengeToken, code: value });
      if (result.status !== 'AUTHENTICATED') {
        throw new ApiError('UNAUTHENTICATED', 'The code could not be verified. Try again.');
      }
      return result.user;
    },
    onSuccess: onAuthenticated,
  });

  function submit(event: SubmitEvent) {
    event.preventDefault();
    const parsed = mfaCodeSchema.safeParse(code);
    if (!parsed.success) {
      setCodeError('Enter the 6-digit code from your authenticator app or a recovery code.');
      return;
    }
    setCodeError(undefined);
    mutation.mutate(parsed.data);
  }

  return (
    <form onSubmit={submit} noValidate className="stack">
      <h2>{phrase('Two-step verification')}</h2>
      <p>
        {phrase('Open your authenticator app and enter the current code for Mekum Founder OS.')}
      </p>
      <TextField
        label={phrase('Authentication code')}
        autoComplete="one-time-code"
        autoFocus
        value={code}
        onValueChange={setCode}
        error={codeError}
        hint={phrase('Lost your device? Enter one of your recovery codes instead.')}
        required
      />
      <ErrorNotice error={mutation.error} />
      <div className="form-actions">
        <button type="submit" className="button" disabled={mutation.isPending}>
          {mutation.isPending ? phrase('Verifying…') : phrase('Verify and sign in')}
        </button>
        <button type="button" className="button button--secondary" onClick={onCancel}>
          {phrase('Start over')}
        </button>
      </div>
    </form>
  );
}
